/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 *
 * Which contract durations the two duration pickers may offer.
 *
 * A duration nobody sells is a guaranteed empty result: the visitor picks it,
 * the list goes blank, and nothing on screen explains why. '4 jaar' was exactly
 * that — offered in both dropdowns, carried by zero provider rows.
 *
 * Deleting it by hand would fix today and rot tomorrow: the scraper, the
 * partner portal and the admin contract editor can all still produce a 4-year
 * aanbod, and a hardcoded list would keep hiding it after it arrives. So the
 * option list is derived from the rows themselves, in one place, for both
 * pickers (ComparisonEngine's results bar and the hero Filters card).
 *
 * Since 2026-10-16 the looptijd is a MULTI-select (Jasper). It started with
 * every looptijd chosen until 2026-10-30; since then it starts with '1 jaar'
 * alone (Jasper: the standard comparison is 1 jaar at 3 000 kWh —
 * DEFAULT_DURATION_SELECTION below, also what an address without the
 * parameter means). The address keeps one parameter: 'Alle' or a comma list
 * of canonical durations, `1 jaar,3 jaar`, so an older link with one looptijd
 * keeps its meaning. The selection rules — read, toggle, label — live at the
 * end of this module and nowhere else.
 */

/** Canonical display order. Also the allowlist: anything else on a row (a typo,
 *  a free-text looptijd) is ignored rather than promoted into the dropdown. */
export const CANONICAL_DURATIONS = ['1 jaar', '2 jaar', '3 jaar', '4 jaar', '5 jaar'] as const;

export type CanonicalDuration = (typeof CANONICAL_DURATIONS)[number];

/** i18n key per canonical duration — the label side of the same list. */
export const DURATION_LABEL_KEYS: Record<CanonicalDuration, string> = {
  '1 jaar': 'filters.1_year',
  '2 jaar': 'filters.2_years',
  '3 jaar': 'filters.3_years',
  '4 jaar': 'filters.4_years',
  '5 jaar': 'filters.5_years',
};

/** Matching mirrors the filter itself, which compares case-insensitively. */
const normalize = (d: string | null | undefined): string => (d ?? '').trim().toLowerCase();

/**
 * The durations worth offering, in canonical order.
 *
 * @param rows      whatever the providers payload currently holds
 * @param selected  the looptijd value in the URL right now ('Alle' or a comma
 *                  list); every duration in it is kept in the list even when
 *                  no row carries it, so an active chip stays deselectable
 *                  instead of becoming a filter with no control.
 *
 * With no rows yet (first paint before /api/providers answers, or a test that
 * renders the hero on its own) nothing is known, so the full canonical list is
 * returned — hiding options on the strength of an empty dataset would be a
 * guess dressed up as a fact.
 */
export function availableDurations(
  rows: ReadonlyArray<{ contractDuration?: string | null }> | null | undefined,
  selected?: string | null
): CanonicalDuration[] {
  const present = new Set<string>();
  for (const row of rows ?? []) {
    const d = normalize(row?.contractDuration);
    if (d) present.add(d);
  }
  if (present.size === 0) return [...CANONICAL_DURATIONS];

  const selectedNorm = new Set((parseDurationSelection(selected) ?? []).map(normalize));
  return CANONICAL_DURATIONS.filter((d) => {
    const norm = normalize(d);
    return present.has(norm) || selectedNorm.has(norm);
  });
}

/** Ready-made `{ label, value }` list, "Alle" first. `t` is the caller's. */
export function durationOptions(
  rows: ReadonlyArray<{ contractDuration?: string | null }> | null | undefined,
  selected: string | null | undefined,
  t: (key: string) => string
): { label: string; value: string }[] {
  return [
    { label: t('filters.all_durations'), value: 'Alle' },
    ...availableDurations(rows, selected).map((d) => ({
      label: t(DURATION_LABEL_KEYS[d]),
      value: d,
    })),
  ];
}

// ——— The selection (multi-select since 2026-10-16) ———————————————————————

/** The value of "every looptijd" — the default from 2026-10-16 to 2026-10-30. */
export const ALL_DURATIONS = 'Alle';

/**
 * The selection a clean visit starts with, and what an absent parameter reads
 * as (src/lib/comparerParams.ts): '1 jaar' alone since 2026-10-30 (Jasper),
 * every looptijd from 2026-10-16, '1 jaar' before that too. A canonical
 * looptijd, never 'Alle': "wissen" and a removed chip come back here.
 */
export const DEFAULT_DURATION_SELECTION = '1 jaar';

/** The value of "no looptijd": "Alle" clicked while every looptijd is ticked
 *  unticks them all (Jasper, 2026-10-16). It matches no row. */
export const NO_DURATIONS = 'Geen';

/**
 * The looptijden a value selects, in the order given — or null for all of
 * them ('Alle', empty, nothing), and [] for none ('Geen'). Duplicates and empty items are dropped; an
 * unknown item is kept (it simply matches no row), as one always was.
 */
export function parseDurationSelection(value: string | null | undefined): string[] | null {
  if (!value || value === ALL_DURATIONS) return null;
  if (value === NO_DURATIONS) return [];
  const seen = new Set<string>();
  const list: string[] = [];
  for (const item of value.split(',')) {
    const d = item.trim();
    if (!d || seen.has(normalize(d))) continue;
    seen.add(normalize(d));
    list.push(d);
  }
  return list.length > 0 ? list : null;
}

/** Whether a value selects this option ('Alle' selects every looptijd; case-insensitive like the filter). */
export function isDurationSelected(value: string | null | undefined, option: string): boolean {
  const list = parseDurationSelection(value);
  if (option === ALL_DURATIONS) return list === null;
  return list === null || list.some((d) => normalize(d) === normalize(option));
}

/**
 * The value after the visitor ticks or unticks one option. `available` is the
 * looptijden on offer (the option list without 'Alle'), in display order.
 *
 * - 'Alle' selects every looptijd again — and unticks them all ('Geen')
 *   when every looptijd is already selected.
 * - Unticking one while all are selected keeps the others.
 * - The last ticked looptijd cannot be unticked: an empty selection would be
 *   an empty list with nothing on screen to say why — the value stays.
 * - A selection that covers every looptijd on offer is 'Alle' again: one
 *   value for "every looptijd", whichever way it was ticked (until
 *   2026-10-30 that was also the default, so the control read as unset).
 *
 * The list is written in display order, whatever order it was ticked in.
 */
export function toggleDurationSelection(
  value: string | null | undefined,
  option: string,
  available: readonly string[]
): string {
  if (option === ALL_DURATIONS) {
    return parseDurationSelection(value) === null ? NO_DURATIONS : ALL_DURATIONS;
  }
  const current = parseDurationSelection(value) ?? [...available];
  const has = current.some((d) => normalize(d) === normalize(option));
  const next = has
    ? current.filter((d) => normalize(d) !== normalize(option))
    : [...current, option];
  if (next.length === 0) return value || ALL_DURATIONS;
  const nextNorm = new Set(next.map(normalize));
  if (available.length > 0 && available.every((d) => nextNorm.has(normalize(d)))) {
    return ALL_DURATIONS;
  }
  const rank = (d: string) => {
    const i = available.findIndex((a) => normalize(a) === normalize(d));
    return i < 0 ? available.length : i;
  };
  return [...next].sort((a, b) => rank(a) - rank(b)).join(',');
}

/**
 * What a closed control says for a value: `allLabel` for every looptijd, the
 * label of one, the labels of two joined ("1 jaar, 3 jaar"), and from three on
 * the count (`countLabel`, formatted by the caller) — none is a count of 0.
 */
export function durationSelectionLabel(
  value: string | null | undefined,
  options: readonly { label: string; value: string }[],
  allLabel: string,
  countLabel: (count: number) => string
): string {
  const list = parseDurationSelection(value);
  if (list === null) return allLabel;
  if (list.length === 0 || list.length >= 3) return countLabel(list.length);
  return list
    .map((d) => options.find((o) => normalize(o.value) === normalize(d))?.label ?? d)
    .join(', ');
}
